import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { TotalRevenueBreakdownTrendItem } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';

export default class TotalRevenueBreakdownTrendReference extends Model {
  [Type] = 'total-revenue/breakdown/trend-reference' as const;

  @belongsTo('total-revenue/breakdown/trend-item', {
    async: false,
    inverse: 'referenceValues',
  })
  declare totalRevenueItem: TotalRevenueBreakdownTrendItem;

  @attr('hoteldate')
  declare referenceFromDate: NonNullable<HoteldateTransformReturn>;
  @attr declare referenceType: string;
  @attr('number') declare value: number;

  @service declare totalRevenue: Services['total-revenue'];

  @tracked capacity?: number;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRelativeChange() {
    return this.totalRevenue.formatRelativeChange(this.relativeChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return this.totalRevenue.formatValue(this.valueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get relativeChange() {
    return this.totalRevenue.calculateRelativeChange(
      this.totalRevenueItem.valueOrPAR,
      this.valueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translatedReferenceType() {
    return this.totalRevenue.translateReferenceType(this.referenceType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueOrPAR() {
    return this.totalRevenue.isPerAvailableRoom
      ? this.totalRevenue.calculatePAR(this.value, this.capacity)
      : this.value;
  }
}
